/**
 * Canvas-drawn map icons, generated on demand via MapLibre's `styleimagemissing` event.
 * Image ids encode everything needed to draw them:
 *   apos:ac:<shape>:<fill|hollow>:<hex>      aircraft (points north; rotated by the layer)
 *   apos:cam:<static|manned|aerial>:<hex>     camera by mount
 *   apos:loc:<kind>:<hex>                     mission location symbol
 *   apos:flag:<hex>                           marker flag
 *   apos:beam:<hex>                           "where I'm facing" cone (apex at the image centre, points north)
 * Drawn at devicePixelRatio (≥2) for crisp edges; a contrasting keyline keeps them legible on
 * satellite imagery.
 */
import type { Map as MlMap } from 'maplibre-gl';

const PR = Math.max(2, Math.min(3, typeof window !== 'undefined' ? window.devicePixelRatio || 2 : 2));

function canvas(size: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = c.height = Math.round(size * PR);
  const ctx = c.getContext('2d')!;
  ctx.scale(PR, PR);
  return [c, ctx];
}

function keyline(hex: string): string {
  // dark ink on light fills, light ink on dark fills
  const n = parseInt(hex.replace('#', '').slice(0, 6).padEnd(6, '0'), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 140 ? 'rgba(0,0,0,0.78)' : 'rgba(255,255,255,0.9)';
}

type Shape = 'heli' | 'arrow';

function pathHeli(ctx: CanvasRenderingContext2D, s: number) {
  const c = s / 2;
  ctx.beginPath();
  ctx.ellipse(c, c - 3, 4.6, 7.5, 0, 0, Math.PI * 2);
  ctx.moveTo(c + 1.3, c + 3.5);
  ctx.lineTo(c + 1.1, c + 13);
  ctx.lineTo(c + 5, c + 14);
  ctx.lineTo(c + 5, c + 15.6);
  ctx.lineTo(c - 5, c + 15.6);
  ctx.lineTo(c - 5, c + 14);
  ctx.lineTo(c - 1.1, c + 13);
  ctx.lineTo(c - 1.3, c + 3.5);
  ctx.closePath();
}

function pathArrow(ctx: CanvasRenderingContext2D, s: number) {
  const c = s / 2;
  ctx.beginPath();
  ctx.moveTo(c, c - 13);
  ctx.lineTo(c + 9, c + 11);
  ctx.lineTo(c, c + 6);
  ctx.lineTo(c - 9, c + 11);
  ctx.closePath();
}

function drawAircraft(shape: Shape, style: 'fill' | 'hollow', hex: string): ImageData {
  const size = 40;
  const [cv, ctx] = canvas(size);
  const path = shape === 'heli' ? pathHeli : pathArrow;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  // keyline
  path(ctx, size);
  ctx.strokeStyle = keyline(hex);
  ctx.lineWidth = style === 'fill' ? 3.2 : 4.4;
  ctx.stroke();
  if (style === 'fill') {
    ctx.fillStyle = hex;
    ctx.fill();
  } else {
    ctx.strokeStyle = hex;
    ctx.lineWidth = 1.8;
    ctx.setLineDash([3, 2]);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  if (shape === 'heli') {
    // rotor disc
    ctx.beginPath();
    ctx.arc(size / 2, size / 2 - 3, 14.5, 0, Math.PI * 2);
    ctx.strokeStyle = keyline(hex);
    ctx.lineWidth = 2.6;
    ctx.stroke();
    ctx.strokeStyle = hex;
    ctx.lineWidth = 1.1;
    ctx.globalAlpha = 0.85;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }
  return ctx.getImageData(0, 0, cv.width, cv.height);
}

export type CamIconMount = 'static' | 'manned' | 'aerial';

/**
 * Camera symbols by mount (all point east; the layer rotates static/manned ones to the heading):
 *   static  camera body on a solid square base (a fixed position) — the boldest
 *   manned  smaller body with the operator's colour ring, sits beside the operator dot
 *   aerial  round badge with a tiny camera, rides with its aircraft (never rotated)
 */
function drawCamera(mount: CamIconMount, hex: string): ImageData {
  const size = mount === 'static' ? 30 : mount === 'manned' ? 22 : 18;
  const [cv, ctx] = canvas(size);
  const k = keyline(hex);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  const c = size / 2;
  const body = (x: number, y: number, w: number, h: number) => {
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 1.5);
    ctx.moveTo(x + w, y + h * 0.33);
    ctx.lineTo(x + w + h * 0.5, y + h * 0.1);
    ctx.lineTo(x + w + h * 0.5, y + h * 0.9);
    ctx.lineTo(x + w, y + h * 0.67);
    ctx.closePath();
  };
  if (mount === 'aerial') {
    ctx.beginPath();
    ctx.arc(c, c, c - 1.5, 0, Math.PI * 2);
    ctx.fillStyle = k;
    ctx.fill();
    ctx.lineWidth = 1.4;
    ctx.strokeStyle = hex;
    ctx.stroke();
    body(c - 5, c - 3, 7, 6);
    ctx.fillStyle = hex;
    ctx.fill();
    return ctx.getImageData(0, 0, cv.width, cv.height);
  }
  if (mount === 'static') {
    // base square at the pivot (the camera's fixed position)
    ctx.beginPath();
    ctx.rect(c - 3.5, c - 3.5, 7, 7);
    ctx.strokeStyle = k;
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.fillStyle = hex;
    ctx.fill();
    body(c + 1, c - 5, 8, 10);
  } else {
    body(c - 1, c - 4, 6.5, 8);
  }
  ctx.strokeStyle = k;
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.fillStyle = hex;
  ctx.fill();
  return ctx.getImageData(0, 0, cv.width, cv.height);
}

/** Facing cone: apex at the centre (the crew dot), opening north; the layer rotates it by the compass. */
function drawBeam(hex: string): ImageData {
  const size = 120;
  const [cv, ctx] = canvas(size);
  const c = size / 2;
  const len = c - 2;
  const half = (28 * Math.PI) / 180;
  const g = ctx.createRadialGradient(c, c, 4, c, c, len);
  const rgb = hex.replace('#', '');
  const n = parseInt(rgb.slice(0, 6).padEnd(6, '0'), 16);
  const col = `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
  g.addColorStop(0, `rgba(${col},0.75)`);
  g.addColorStop(0.55, `rgba(${col},0.32)`);
  g.addColorStop(1, `rgba(${col},0)`);
  ctx.beginPath();
  ctx.moveTo(c, c);
  ctx.arc(c, c, len, -Math.PI / 2 - half, -Math.PI / 2 + half);
  ctx.closePath();
  ctx.fillStyle = g;
  ctx.fill();
  return ctx.getImageData(0, 0, cv.width, cv.height);
}

function drawFlag(hex: string): ImageData {
  const size = 22;
  const [cv, ctx] = canvas(size);
  const k = keyline(hex);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const shape = () => {
    ctx.beginPath();
    ctx.moveTo(7, 19);
    ctx.lineTo(7, 3.5);
    ctx.lineTo(17, 3.5);
    ctx.lineTo(14.5, 7.5);
    ctx.lineTo(17, 11.5);
    ctx.lineTo(7, 11.5);
  };
  shape();
  ctx.strokeStyle = k;
  ctx.lineWidth = 3.4;
  ctx.stroke();
  shape();
  ctx.fillStyle = hex;
  ctx.fill();
  ctx.strokeStyle = hex;
  ctx.lineWidth = 1.6;
  ctx.stroke();
  return ctx.getImageData(0, 0, cv.width, cv.height);
}

function drawLocation(kind: string, hex: string): ImageData {
  const size = kind === 'camera_site' ? 30 : 22;
  const [cv, ctx] = canvas(size);
  const c = size / 2;
  const k = keyline(hex);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  const strokeFill = (fill: boolean, lw = 1.6) => {
    ctx.strokeStyle = k;
    ctx.lineWidth = lw + 2.4;
    ctx.stroke();
    if (fill) {
      ctx.fillStyle = hex;
      ctx.fill();
    }
    ctx.strokeStyle = hex;
    ctx.lineWidth = lw;
    ctx.stroke();
  };
  switch (kind) {
    case 'camera_site': {
      // bold square frame with a lens dot — the most prominent place symbol
      ctx.beginPath();
      ctx.rect(c - 10, c - 10, 20, 20);
      strokeFill(false, 2.2);
      ctx.beginPath();
      ctx.arc(c, c, 4, 0, Math.PI * 2);
      strokeFill(true, 1);
      // corner ticks
      ctx.beginPath();
      for (const [x, y] of [
        [c - 14, c],
        [c + 14, c],
        [c, c - 14],
        [c, c + 14],
      ] as const) {
        ctx.moveTo(x, y);
        ctx.lineTo(x + (x < c ? 3 : x > c ? -3 : 0), y + (y < c ? 3 : y > c ? -3 : 0));
      }
      strokeFill(false, 1.6);
      break;
    }
    case 'staging':
      ctx.beginPath();
      ctx.moveTo(c, c - 7);
      ctx.lineTo(c + 7, c + 6);
      ctx.lineTo(c - 7, c + 6);
      ctx.closePath();
      strokeFill(false);
      break;
    case 'basecamp':
      ctx.beginPath();
      ctx.moveTo(c - 7, c + 6);
      ctx.lineTo(c, c - 7);
      ctx.lineTo(c + 7, c + 6);
      ctx.closePath();
      ctx.moveTo(c, c - 1);
      ctx.lineTo(c, c + 6);
      strokeFill(true);
      break;
    case 'landing_zone':
      ctx.beginPath();
      ctx.arc(c, c, 8, 0, Math.PI * 2);
      strokeFill(false);
      ctx.beginPath();
      ctx.moveTo(c - 3.5, c - 4);
      ctx.lineTo(c - 3.5, c + 4);
      ctx.moveTo(c + 3.5, c - 4);
      ctx.lineTo(c + 3.5, c + 4);
      ctx.moveTo(c - 3.5, c);
      ctx.lineTo(c + 3.5, c);
      strokeFill(false, 1.6);
      break;
    case 'hazard':
      ctx.beginPath();
      ctx.moveTo(c, c - 8);
      ctx.lineTo(c + 8, c + 6.5);
      ctx.lineTo(c - 8, c + 6.5);
      ctx.closePath();
      strokeFill(true);
      ctx.beginPath();
      ctx.moveTo(c, c - 3);
      ctx.lineTo(c, c + 1.5);
      ctx.moveTo(c, c + 4);
      ctx.lineTo(c, c + 4.2);
      ctx.strokeStyle = keyline(hex);
      ctx.lineWidth = 1.8;
      ctx.stroke();
      break;
    case 'parking':
      ctx.beginPath();
      ctx.roundRect(c - 7, c - 7, 14, 14, 2);
      strokeFill(false);
      ctx.beginPath();
      ctx.moveTo(c - 2.5, c + 4);
      ctx.lineTo(c - 2.5, c - 4);
      ctx.lineTo(c + 1, c - 4);
      ctx.arc(c + 1, c - 1.5, 2.5, -Math.PI / 2, Math.PI / 2);
      ctx.lineTo(c - 2.5, c + 1);
      strokeFill(false, 1.4);
      break;
    default:
      // poi: diamond
      ctx.beginPath();
      ctx.moveTo(c, c - 7);
      ctx.lineTo(c + 7, c);
      ctx.lineTo(c, c + 7);
      ctx.lineTo(c - 7, c);
      ctx.closePath();
      strokeFill(true);
  }
  return ctx.getImageData(0, 0, cv.width, cv.height);
}

export function aircraftIconId(shape: Shape, hollow: boolean, hex: string): string {
  return `apos:ac:${shape}:${hollow ? 'hollow' : 'fill'}:${hex.toLowerCase()}`;
}
export const cameraIconId = (mount: CamIconMount, hex: string) => `apos:cam:${mount}:${hex.toLowerCase()}`;
export const flagIconId = (hex: string) => `apos:flag:${hex.toLowerCase()}`;
export const beamIconId = (hex: string) => `apos:beam:${hex.toLowerCase()}`;
export const locationIconId = (kind: string, hex: string) => `apos:loc:${kind}:${hex.toLowerCase()}`;

/** Lazily create any `apos:*` image the style asks for. */
export function installIconFactory(map: MlMap): () => void {
  const handler = (e: { id: string }) => {
    const id = e.id;
    if (map.hasImage(id)) return;
    if (!id.startsWith('apos:')) {
      // basemap styles occasionally reference sprites they don't ship (e.g. "wood-pattern")
      map.addImage(id, { width: 1, height: 1, data: new Uint8Array(4) });
      return;
    }
    const parts = id.split(':');
    let img: ImageData | null = null;
    try {
      if (parts[1] === 'ac') img = drawAircraft(parts[2] as Shape, parts[3] === 'hollow' ? 'hollow' : 'fill', parts[4]!);
      else if (parts[1] === 'cam') img = drawCamera(parts[2] as CamIconMount, parts[3]!);
      else if (parts[1] === 'flag') img = drawFlag(parts[2]!);
      else if (parts[1] === 'beam') img = drawBeam(parts[2]!);
      else if (parts[1] === 'loc') img = drawLocation(parts[2]!, parts[3]!);
    } catch {
      img = null;
    }
    if (img) map.addImage(id, img, { pixelRatio: PR });
  };
  map.on('styleimagemissing', handler);
  return () => map.off('styleimagemissing', handler);
}
